import { extractErrorMessage } from "@api/axios";
import { fetchCategories, createCategory } from "@/store/categoriesSlice";
import {
  fetchSubcategories,
  createSubcategory,
} from "@/store/subcategoriesSlice";
import type { AppDispatch } from "@/store";
import type { Category, EntryKind } from "@/types";
import { manageCategoriesCsvHeadersMatch } from "./categoriesCsv";
import { parseCsv } from "./csvParse";
import { parseCsvEntryKind } from "./csvEntryKind";

const N_COLS = 5;

export interface ManageCategoriesImportResult {
  categoriesCreated: number;
  subcategoriesCreated: number;
  skippedRows: number;
  errors: string[];
  fatalReason: "none" | "bad_header" | "empty_file";
}

interface CatAgg {
  kind: EntryKind;
  categoryName: string;
  categoryDescription?: string;
  /** First-seen order, unique sub names */
  subs: { name: string; description?: string }[];
}

function groupKey(kind: EntryKind, categoryNameTrimmed: string): string {
  return `${kind}\x00${categoryNameTrimmed}`;
}

export async function runManageCategoriesCsvImport(
  dispatch: AppDispatch,
  csvText: string,
): Promise<ManageCategoriesImportResult> {
  const emptyResult = (
    fatal: ManageCategoriesImportResult["fatalReason"],
  ): ManageCategoriesImportResult => ({
    categoriesCreated: 0,
    subcategoriesCreated: 0,
    skippedRows: 0,
    errors: [],
    fatalReason: fatal,
  });

  if (csvText.trim() === "") return emptyResult("empty_file");

  const rows = parseCsv(csvText);
  if (rows.length === 0) return emptyResult("empty_file");

  const header = rows[0]!;
  if (!manageCategoriesCsvHeadersMatch(header)) {
    return {
      categoriesCreated: 0,
      subcategoriesCreated: 0,
      skippedRows: 0,
      errors: [],
      fatalReason: "bad_header",
    };
  }

  const groups = new Map<string, CatAgg>();
  let skippedRows = 0;
  const rowErrors: string[] = [];

  for (let i = 1; i < rows.length; i++) {
    const cells = rows[i]!;
    const lineNo = i + 1;
    if (cells.length !== N_COLS) {
      skippedRows++;
      rowErrors.push(
        `${lineNo}: expected ${N_COLS} columns, got ${cells.length}.`,
      );
      continue;
    }

    const kind = parseCsvEntryKind(cells[0]!);
    const catName = cells[1]!.trim();
    const catDesc = cells[2]!.trim();
    const subName = cells[3]!.trim();
    const subDesc = cells[4]!.trim();

    if (!kind) {
      skippedRows++;
      rowErrors.push(`${lineNo}: invalid kind "${cells[0]}".`);
      continue;
    }

    if (!catName) {
      skippedRows++;
      rowErrors.push(`${lineNo}: missing category_name.`);
      continue;
    }

    const key = groupKey(kind, catName);
    let g = groups.get(key);
    if (!g) {
      g = {
        kind,
        categoryName: catName,
        categoryDescription: catDesc || undefined,
        subs: [],
      };
      groups.set(key, g);
    } else if (!g.categoryDescription && catDesc) {
      g.categoryDescription = catDesc;
    }

    if (subName) {
      if (!g.subs.some((s) => s.name === subName)) {
        g.subs.push({
          name: subName,
          description: subDesc || undefined,
        });
      }
    }
  }

  let categoriesCreated = 0;
  let subcategoriesCreated = 0;
  const errors = [...rowErrors];

  let workingCats: Category[] = [
    ...(await dispatch(fetchCategories()).unwrap()),
  ];
  let workingSubs = [...(await dispatch(fetchSubcategories()).unwrap())];

  const sortedGroups = [...groups.values()].sort((a, b) =>
    a.categoryName.localeCompare(b.categoryName),
  );

  for (const agg of sortedGroups) {
    let cat = workingCats.find(
      (c) =>
        (c.kind ?? "expense") === agg.kind &&
        c.name.trim() === agg.categoryName,
    );

    if (!cat) {
      try {
        cat = await dispatch(
          createCategory({
            name: agg.categoryName,
            description: agg.categoryDescription,
            kind: agg.kind,
          }),
        ).unwrap();
        categoriesCreated++;
        workingCats.push(cat);
      } catch (e: unknown) {
        errors.push(
          `${agg.kind} / "${agg.categoryName}": ${extractErrorMessage(e)}`,
        );
        continue;
      }
    }

    for (const sub of agg.subs) {
      const exists = workingSubs.some(
        (s) => s.categoryId === cat!.id && s.name.trim() === sub.name,
      );
      if (exists) continue;
      try {
        const created = await dispatch(
          createSubcategory({
            name: sub.name,
            description: sub.description,
            categoryId: cat!.id,
          }),
        ).unwrap();
        subcategoriesCreated++;
        workingSubs.push(created);
      } catch (e: unknown) {
        errors.push(
          `Sub "${sub.name}" under "${agg.categoryName}": ${extractErrorMessage(e)}`,
        );
      }
    }
  }

  await dispatch(fetchCategories()).unwrap();
  await dispatch(fetchSubcategories()).unwrap();

  return {
    categoriesCreated,
    subcategoriesCreated,
    skippedRows,
    errors,
    fatalReason: "none",
  };
}
